import type { ClientMethod } from 'openapi-fetch';
import type { HttpMethod, MediaType } from 'openapi-typescript-helpers';

import { client } from '@connected/api-client';
import { getInitPromise } from '@connected/api-client/components/providers/media-manager';
import { urls } from '@connected/api-client/generated/urls.generated';
import { createScopeId } from '@connected/api-client/lib/utils';
import { toApiHttpMethod } from '@connected/api-client/utils';
import { QueryClient } from '@tanstack/react-query';

import type { FileUploadMutationOptions, SelectableEndpoint } from '@/hooks/use-electrical-project-file-upload';

import { MAX_AGE, STALE_TIME } from '@/config/query-client';
import { handleMutation } from '@/hooks/use-electrical-project-file-upload';
import { logger } from '@/lib/logger';

import { generateMediaFileId } from './electrical-file';

type InitWithUnknowns<Init> = Init & { [key: string]: unknown };
type Paths = Record<string, Record<HttpMethod, object>>;

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: MAX_AGE,
            staleTime: STALE_TIME,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            refetchOnMount: true,
            networkMode: 'offlineFirst',
        },
    },
});

logger.info({
    system: 'queryClient',
    message: 'Query client initialized, beginning to register mutations',
});

declare global {
    // eslint-disable-next-line ts/consistent-type-definitions
    interface Window {
        registeredMutations: Set<string> | null;
    }
}

if (import.meta.env.DEV) {
    window.registeredMutations = window.registeredMutations || new Set();
}

for (const { url, methods } of urls) {
    if (url.startsWith('/api/v2/electrical/projects/files/upload/')) {
        window.registeredMutations?.add(url);

        queryClient.setMutationDefaults(['post', url], {
            mutationFn: async ({ query }: FileUploadMutationOptions<SelectableEndpoint>) => {
                const mediaManager = await getInitPromise();

                if (!query.ElectricalProjectFileId || !query.ElectricalProjectVersionId) {
                    logger.error({
                        system: 'registerMutationFiles',
                        message: 'Electrical project file ID and version ID are required',
                        query,
                    }, { apiUrl: url, apiHttpMethod: 'Post' });
                    return null;
                }

                // Get the media file based on the electrical project file
                const mediaFileId = generateMediaFileId(query.ElectricalProjectVersionId, query.ElectricalProjectFileId);
                const mediaFile = await mediaManager.getMediaFile(mediaFileId);

                if (!mediaFile) {
                    logger.error({
                        system: 'ElectricalProjectFilesProvider',
                        message: 'Media file not found',
                        mediaFileId,
                    }, { apiUrl: url, apiHttpMethod: 'Post' });
                    return null;
                }

                // Retrieve the file from the media manager
                const file = await mediaManager.download(mediaFile);

                logger.info({
                    system: 'ElectricalProjectFilesProvider',
                    message: 'Retrieved file from media manager',
                    mediaFileId,
                }, { apiUrl: url, apiHttpMethod: 'Post' });

                const result = await handleMutation(url as SelectableEndpoint, {
                    file,
                    query,
                });

                return result;
            },
            scope: {
                id: createScopeId('defaultMutation'),
            },
        });

        continue;
    }
    for (const method of methods.filter(method => method !== 'get') as HttpMethod[]) {
        window.registeredMutations?.add(url);

        queryClient.setMutationDefaults([method.toLowerCase(), url], {
            mutationFn: async (init) => {
                const mth = method.toUpperCase() as Uppercase<typeof method>;
                const fn = client[mth] as unknown as ClientMethod<Paths, HttpMethod, MediaType>;

                const { data, error } = await fn(url, init as InitWithUnknowns<typeof init>);

                logger.debug({
                    system: 'registerMutationDefault',
                    message: 'Mutation response',
                    data,
                    error,
                }, {
                    apiUrl: url,
                    apiHttpMethod: toApiHttpMethod(method),
                });

                return data;
            },
            onError: (error) => {
                logger.error({
                    system: 'registerMutationDefault',
                    message: 'Mutation error',
                    error,
                }, {
                    apiUrl: url,
                    apiHttpMethod: toApiHttpMethod(method),
                });
            },
            onSuccess: (data) => {
                logger.debug({
                    system: 'registerMutationDefault',
                    message: 'Mutation success',
                    data,
                }, {
                    apiUrl: url,
                    apiHttpMethod: toApiHttpMethod(method),
                });
            },
            scope: {
                id: createScopeId('defaultMutation'),
            },
        });
    }
}

logger.info({
    system: 'queryClient',
    message: 'Registered mutations',
    registeredMutations: window.registeredMutations?.size,
});
